/**
 * Product Video Slider Block
 * Handles video slider functionality and HLS video playback in popup modal
 */

(function() {
  'use strict';

  function isHlsUrl(src) {
    if (typeof src !== 'string' || !src) return false;
    return (
      /\.m3u8(\?|$|#)/i.test(src) ||
      src.indexOf('m3u8') !== -1
    );
  }

  /** Prefer Liquid data-video-stream="hls" when Shopify format was not .m3u8 in the URL string */
  function shouldPlayHls(url, streamAttr) {
    if (streamAttr === 'hls') return true;
    return isHlsUrl(url);
  }

  function canPlayNativeHls(video) {
    if (!video || !video.canPlayType) return false;
    return !!(
      video.canPlayType('application/vnd.apple.mpegurl') ||
      video.canPlayType('application/x-mpegURL')
    );
  }

  function loadHlsScript() {
    return new Promise(function (resolve, reject) {
      if (typeof Hls !== 'undefined') {
        resolve();
        return;
      }
      var s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.15/dist/hls.min.js';
      s.async = true;
      s.onload = function () {
        resolve();
      };
      s.onerror = function () {
        reject(new Error('hls.js load failed'));
      };
      document.head.appendChild(s);
    });
  }

  class ProductVideoSlider {
    constructor(container) {
      this.container = container;
      this.track = container.querySelector('[data-slider-track]');
      this.items = container.querySelectorAll('.product-video-slider__item');
      this.prevBtn = container.querySelector('[data-slider-prev]');
      this.nextBtn = container.querySelector('[data-slider-next]');
      // Modal might be inside container or as sibling - try both
      // Modal is a sibling element, not a child - find it by ID
      const sliderId = container.getAttribute('data-video-slider-id');
      // Try multiple ways to find the modal
      this.modal = document.querySelector(`#video-modal-${sliderId}`) || 
                    document.querySelector(`[data-video-modal][id*="${sliderId}"]`) ||
                    container.nextElementSibling?.querySelector('[data-video-modal]') ||
                    container.parentElement?.querySelector(`#video-modal-${sliderId}`);
      this.videoElement = null; // Will be created dynamically when video opens
      this.modalCloseBtns = this.modal ? this.modal.querySelectorAll('[data-modal-close]') : [];
      
      if (!this.modal) {
        console.warn('Modal not found during initialization:', {
          sliderId,
          container: container,
          nextSibling: container.nextElementSibling,
          parent: container.parentElement
        });
      }
      
      this.currentIndex = 0;
      this.itemsPerView = this.calculateItemsPerView();
      this.maxIndex = Math.max(0, this.items.length - this.itemsPerView);
      
      this.init();
    }

    calculateItemsPerView() {
      const itemWidth = this.items[0]?.offsetWidth || 200;
      const containerWidth = this.track.parentElement.offsetWidth;
      return Math.floor(containerWidth / (itemWidth + 16)); // 16px gap
    }

    init() {
      if (!this.track || this.items.length === 0) {
        console.warn('Video slider init failed:', {
          hasTrack: !!this.track,
          itemsCount: this.items.length,
          hasModal: !!this.modal
        });
        return;
      }
      
      console.log('Video slider initializing:', {
        items: this.items.length,
        hasModal: !!this.modal,
        modalId: this.modal?.id
      });

      // Check if slider should be enabled (more than 3 videos)
      const videoCount = parseInt(this.container.getAttribute('data-video-count')) || this.items.length;
      const isSliderEnabled = videoCount > 3;

      // Initialize slider navigation only if more than 3 videos
      if (isSliderEnabled) {
        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', () => this.slidePrev());
        }
        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', () => this.slideNext());
        }

        // Handle window resize for slider
        let resizeTimeout;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            this.itemsPerView = this.calculateItemsPerView();
            this.maxIndex = Math.max(0, this.items.length - this.itemsPerView);
            this.updateSlider();
          }, 250);
        });

        // Initial slider update
        this.updateSlider();
      } else {
        // Disable slider functionality - just show all videos
        if (this.prevBtn) this.prevBtn.style.display = 'none';
        if (this.nextBtn) this.nextBtn.style.display = 'none';
      }

      // Initialize video elements in slider and click handlers
      this.items.forEach((item, index) => {
        const videoWrapper = item.querySelector('.product-video-slider__video-wrapper');
        const thumbnail = item.querySelector('.product-video-slider__thumbnail');
        const playButton = item.querySelector('.product-video-slider__play-button');
        const videoElement = item.querySelector('.product-video-slider__video');
        
        // Prevent drag events on video elements and items
        if (videoElement) {
          videoElement.addEventListener('dragstart', (e) => {
            e.preventDefault();
            return false;
          });
          videoElement.setAttribute('draggable', 'false');
        }
        
        // Prevent drag on the item itself
        item.addEventListener('dragstart', (e) => {
          e.preventDefault();
          return false;
        });
        item.setAttribute('draggable', 'false');
        
        // Initialize autoplay video in slider if video element exists
        if (videoElement) {
          const wrapper = videoWrapper || thumbnail;
          if (wrapper) {
            const videoHls = wrapper.getAttribute('data-video-hls');
            const videoSrc = wrapper.getAttribute('data-video-src');
            const videoStream = wrapper.getAttribute('data-video-stream');
            const videoUrl = videoHls || videoSrc;
            
            if (videoUrl && videoElement.getAttribute('data-hls-enabled')) {
              var self = this;
              var initVideo = function () {
                if (!shouldPlayHls(videoUrl, videoStream)) {
                  self.initSliderVideo(videoElement, videoUrl, videoStream);
                  return;
                }
                if (canPlayNativeHls(videoElement)) {
                  self.initSliderVideo(videoElement, videoUrl, videoStream);
                  return;
                }
                loadHlsScript()
                  .then(function () {
                    self.initSliderVideo(videoElement, videoUrl, videoStream);
                  })
                  .catch(function () {
                    if (videoSrc) {
                      self.initSliderVideo(videoElement, videoSrc, 'progressive');
                    }
                  });
              };
              initVideo();
            }
          }
        }
        
        // Add click handler to video wrapper or thumbnail
        const clickTarget = videoWrapper || thumbnail;
        if (clickTarget) {
          clickTarget.style.cursor = 'pointer';
          clickTarget.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Video clicked, index:', index);
            this.openVideo(index);
          });
        }
        
        // Add click handler to play button
        if (playButton) {
          playButton.style.cursor = 'pointer';
          playButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Play button clicked, index:', index);
            this.openVideo(index);
          });
        }
      });

      // Initialize modal close
      this.modalCloseBtns.forEach(btn => {
        btn.addEventListener('click', () => this.closeModal());
      });

      // Close modal on ESC key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal?.classList.contains('active')) {
          this.closeModal();
        }
      });
    }

    slidePrev() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.updateSlider();
      }
    }

    slideNext() {
      if (this.currentIndex < this.maxIndex) {
        this.currentIndex++;
        this.updateSlider();
      }
    }

    updateSlider() {
      if (!this.track) return;

      const itemWidth = this.items[0]?.offsetWidth || 200;
      const gap = 16; // 16px gap
      const translateX = -(this.currentIndex * (itemWidth + gap));
      
      this.track.style.transform = `translateX(${translateX}px)`;

      // Update button states
      if (this.prevBtn) {
        this.prevBtn.disabled = this.currentIndex === 0;
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
      }
    }

    initSliderVideo(videoElement, videoUrl, streamAttr) {
      if (!videoElement || !videoUrl) return;

      videoElement.muted = true;
      videoElement.loop = true;
      videoElement.autoplay = true;
      videoElement.playsInline = true;

      if (videoElement.hlsInstance && !videoElement.hlsInstance.isNative) {
        try {
          videoElement.hlsInstance.destroy();
        } catch (e) {}
        videoElement.hlsInstance = null;
      }

      if (!shouldPlayHls(videoUrl, streamAttr)) {
        videoElement.src = videoUrl;
        videoElement.play().catch(function (e) {
          console.warn('Slider video autoplay prevented:', e);
        });
        return;
      }

      if (canPlayNativeHls(videoElement)) {
        videoElement.src = videoUrl;
        videoElement.hlsInstance = { isNative: true };
        videoElement.play().catch(function (e) {
          console.warn('Slider video autoplay prevented:', e);
        });
        return;
      }

      if (typeof Hls !== 'undefined' && Hls.isSupported()) {
        var hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          maxBufferLength: 30,
          capLevelToPlayerSize: true
        });
        hls.loadSource(videoUrl);
        hls.attachMedia(videoElement);
        videoElement.hlsInstance = hls;
        hls.on(Hls.Events.MANIFEST_PARSED, function () {
          videoElement.play().catch(function (e) {
            console.warn('Slider video autoplay prevented:', e);
          });
        });
      } else {
        videoElement.src = videoUrl;
        videoElement.play().catch(function (e) {
          console.warn('Slider video autoplay prevented:', e);
        });
      }
    }

    openVideo(index) {
      const item = this.items[index];
      if (!item) {
        console.warn('Video item not found at index:', index);
        return;
      }

      const videoWrapper = item.querySelector('.product-video-slider__video-wrapper');
      const thumbnail = item.querySelector('.product-video-slider__thumbnail');
      const wrapper = videoWrapper || thumbnail;
      if (!wrapper) {
        console.warn('Video wrapper or thumbnail not found in item:', item);
        return;
      }

      // Get video URLs using Shop by Look pattern
      const videoHls = wrapper.getAttribute('data-video-hls');
      const videoSrc = wrapper.getAttribute('data-video-src');
      const videoStream = wrapper.getAttribute('data-video-stream');
      const fallbackMp4 = wrapper.getAttribute('data-video-fallback-mp4');
      const isShopifyMedia = item.getAttribute('data-is-shopify-media') === 'true';
      const shopifyMediaId = wrapper.getAttribute('data-shopify-media-id');
      
      // Use HLS URL if available, otherwise fall back to regular video source
      const videoUrl = videoHls || videoSrc;
      
      console.log('Opening video:', { index, videoHls, videoSrc, videoStream, videoUrl, isShopifyMedia });

      if (!videoUrl && !isShopifyMedia) {
        console.warn('No video URL found and not a Shopify media object');
        return;
      }

      // Show modal - try to find it again if not found during init
      if (!this.modal) {
        const sliderId = this.container.getAttribute('data-video-slider-id');
        this.modal = document.querySelector(`#video-modal-${sliderId}`);
        if (this.modal) {
          this.modalCloseBtns = this.modal.querySelectorAll('[data-modal-close]');
          // Re-initialize close handlers
          this.modalCloseBtns.forEach(btn => {
            btn.addEventListener('click', () => this.closeModal());
          });
        }
      }
      
      if (!this.modal) {
        console.error('Modal not found! Looking for:', {
          selector: `#video-modal-${this.container.getAttribute('data-video-slider-id')}`,
          sliderId: this.container.getAttribute('data-video-slider-id'),
          allModals: document.querySelectorAll('[data-video-modal]').length
        });
        return;
      }

      // Get video container
      const videoContainer = this.modal.querySelector('[data-video-container]');
      if (!videoContainer) {
        console.error('Video container not found! Looking for [data-video-container]');
        return;
      }

      // Clear previous video
      videoContainer.innerHTML = '';

      // Create video element matching Shopify video_tag format
      // For modal: show controls and enable sound
      const video = document.createElement('video');
      video.className = 'product-video-slider__modal-video';
      video.setAttribute('autoplay', '');
      video.setAttribute('loop', '');
      video.setAttribute('muted', ''); // Start muted for autoplay, will unmute after play
      video.setAttribute('controls', ''); // Show controls in modal
      video.setAttribute('playsinline', '');
      video.setAttribute('preload', 'auto');
      video.setAttribute('data-hls-enabled', 'true');
      video.setAttribute('data-video-element', '');

      videoContainer.appendChild(video);
      this.videoElement = video;

      // Add event listener to unmute when video actually starts playing
      const unmuteOnPlay = () => {
        if (this.videoElement) {
          this.videoElement.muted = false;
        }
      };
      video.addEventListener('playing', unmuteOnPlay, { once: true });

      // Show modal IMMEDIATELY
      this.modal.classList.add('active');
      document.body.style.overflow = 'hidden';

      // Load and play video IMMEDIATELY in background
      if (videoUrl) {
        this.loadHLSVideo(videoUrl, fallbackMp4, videoStream).catch(() => {});
      } else {
        console.warn('No video URL to load');
      }
    }

    closeModal() {
      if (this.modal) {
        this.modal.classList.remove('active');
        document.body.style.overflow = '';
      }

      // Pause, mute, and cleanup video
      if (this.videoElement) {
        // Pause the video
        this.videoElement.pause();
        // Mute the video
        this.videoElement.muted = true;
        // Clear source
        this.videoElement.src = '';
        this.videoElement.load();
        
        // Cleanup HLS instance if exists
        if (this.videoElement.hlsInstance && !this.videoElement.hlsInstance.isNative) {
          this.videoElement.hlsInstance.destroy();
          this.videoElement.hlsInstance = null;
        }
        
        // Remove video element
        const videoContainer = this.modal?.querySelector('[data-video-container]');
        if (videoContainer) {
          videoContainer.innerHTML = '';
        }
        this.videoElement = null;
      }
    }

    loadHLSVideo(videoUrl, fallbackMp4, streamAttr) {
      if (!this.videoElement) return Promise.reject('No video element');

      var self = this;
      return new Promise(function (resolve, reject) {
        var run = function () {
          self.initHLSVideo(videoUrl, resolve, reject, fallbackMp4, streamAttr);
        };
        if (!shouldPlayHls(videoUrl, streamAttr)) {
          run();
          return;
        }
        if (canPlayNativeHls(self.videoElement)) {
          run();
          return;
        }
        if (typeof Hls !== 'undefined' && Hls.isSupported()) {
          run();
          return;
        }
        loadHlsScript()
          .then(run)
          .catch(function () {
            if (fallbackMp4) {
              self.initHLSVideo(fallbackMp4, resolve, reject, null, 'progressive');
            } else {
              run();
            }
          });
      });
    }

    initHLSVideo(videoUrl, resolve, reject, fallbackMp4, streamAttr) {
      var self = this;
      if (!this.videoElement) {
        if (reject) reject('No video element');
        return;
      }

      this.videoElement.muted = true;
      this.videoElement.loop = true;
      this.videoElement.autoplay = true;
      this.videoElement.playsInline = true;
      this.videoElement.preload = 'auto';
      this.videoElement.controls = true;

      function playProgressive(url) {
        self.videoElement.src = url;
        self.videoElement.hlsInstance = null;
        self.videoElement.load();
        var p = self.videoElement.play();
        if (p !== undefined) {
          p.then(function () {
            self.videoElement.muted = false;
            if (resolve) resolve();
          }).catch(function (e) {
            console.warn('Autoplay prevented:', e);
            if (resolve) resolve();
          });
        } else {
          self.videoElement.muted = false;
          if (resolve) resolve();
        }
      }

      if (!videoUrl) {
        if (reject) reject('No URL');
        return;
      }

      if (!shouldPlayHls(videoUrl, streamAttr)) {
        playProgressive(videoUrl);
        return;
      }

      if (canPlayNativeHls(this.videoElement)) {
        this.videoElement.src = videoUrl;
        this.videoElement.hlsInstance = { isNative: true };
        this.videoElement.load();
        var np = this.videoElement.play();
        if (np !== undefined) {
          np.then(function () {
            self.videoElement.muted = false;
            if (resolve) resolve();
          }).catch(function (e) {
            console.warn('Autoplay prevented:', e);
            if (resolve) resolve();
          });
        } else {
          this.videoElement.muted = false;
          if (resolve) resolve();
        }
        return;
      }

      if (typeof Hls !== 'undefined' && Hls.isSupported()) {
        var hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          maxBufferLength: 10,
          maxMaxBufferLength: 30,
          capLevelToPlayerSize: true
        });

        hls.loadSource(videoUrl);
        hls.attachMedia(this.videoElement);
        this.videoElement.hlsInstance = hls;

        function tryFallbackOrReject() {
          hls.destroy();
          self.videoElement.hlsInstance = null;
          if (fallbackMp4) {
            self.initHLSVideo(fallbackMp4, resolve, reject, null, 'progressive');
          } else if (reject) {
            reject('HLS failed');
          }
        }

        hls.on(Hls.Events.ERROR, function (event, data) {
          if (!data.fatal) return;
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              if (data.response && data.response.code === 404) {
                tryFallbackOrReject();
              } else {
                hls.startLoad();
              }
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              tryFallbackOrReject();
              break;
          }
        });

        hls.on(Hls.Events.MANIFEST_PARSED, function () {
          self.videoElement.muted = true;
          self.videoElement.loop = true;
          self.videoElement.controls = true;
          var pp = self.videoElement.play();
          if (pp !== undefined) {
            pp.then(function () {
              self.videoElement.muted = false;
              if (resolve) resolve();
            }).catch(function (e) {
              console.warn('Autoplay prevented:', e);
              if (resolve) resolve();
            });
          } else {
            self.videoElement.muted = false;
            if (resolve) resolve();
          }
        });
        return;
      }

      if (fallbackMp4) {
        playProgressive(fallbackMp4);
        return;
      }

      console.warn('HLS not supported and no MP4 fallback');
      playProgressive(videoUrl);
    }
  }

  // Initialize all video sliders when DOM is ready
  function initVideoSliders() {
    const sliders = document.querySelectorAll('.product-video-slider-block');
    console.log('Found video slider blocks:', sliders.length);
    sliders.forEach((container, index) => {
      if (!container.videoSliderInstance) {
        console.log(`Initializing video slider ${index + 1}`);
        container.videoSliderInstance = new ProductVideoSlider(container);
        console.log(`Video slider ${index + 1} initialized:`, {
          items: container.videoSliderInstance.items.length,
          modal: !!container.videoSliderInstance.modal,
          track: !!container.videoSliderInstance.track
        });
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVideoSliders);
  } else {
    initVideoSliders();
  }

  // Also initialize sliders that are added dynamically
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          if (node.classList && node.classList.contains('product-video-slider-block')) {
            setTimeout(initVideoSliders, 100);
          } else if (node.querySelectorAll) {
            const sliders = node.querySelectorAll('.product-video-slider-block');
            if (sliders.length > 0) {
              setTimeout(initVideoSliders, 100);
            }
          }
        }
      });
    });
  });

  // Start observing
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();
